 import React, { useState, useMemo, useEffect, useRef } from 'react';
 


/**
 * MOCK DATA: JOBS & CANDIDATES
 */
const MOCK_JOBS = [
  { id: 'J-101', title: 'Senior React Developer', description: 'Requires 5+ years of React experience, deep knowledge of hooks, and performance optimization. Must have experience with Redux and Node.js. strict compliance with WCAG 2.1 is required.' },
  { id: 'J-102', title: 'Product Manager', description: 'Seeking a PM with experience in AI-driven tools and cross-functional team leadership. Must have owned a product roadmap from 0 to 1.' },
  { id: 'J-103', title: 'UX Designer', description: 'Focus on accessibility, design systems, and rapid prototyping in Figma. Experience with user research and usability testing is mandatory.' },
  { id: 'J-104', title: 'Data Scientist', description: 'Experience with Python, PyTorch, and large language models. PhD preferred.' },
  { id: 'J-105', title: 'DevOps Engineer', description: 'Strong background in AWS, Kubernetes, and CI/CD pipelines. Terraform expertise required.' }
];


const MOCK_CANDIDATES = [
  { id: 'C-01', jobId: 'J-101', name: 'Alice Chen', experience: '6 years', skills: 'React, Redux, Node.js' },
  { id: 'C-02', jobId: 'J-101', name: 'Bob Smith', experience: '4 years', skills: 'React, TypeScript, CSS' },
  { id: 'C-03', jobId: 'J-102', name: 'Charlie Brown', experience: '7 years', skills: 'Agile, Roadmap Planning, Python' },
  { id: 'C-04', jobId: 'J-103', name: 'Diana Prince', experience: '5 years', skills: 'Figma, Design Systems, WCAG' },
  { id: 'C-05', jobId: 'J-104', name: 'Evan Wright', experience: '3 years', skills: 'Python, R, SQL' },
  { id: 'C-06', jobId: 'J-105', name: 'Fiona Gallagher', experience: '8 years', skills: 'AWS, Docker, Jenkins' }
];


const AVAILABLE_MODELS = [
  { id: 'gpt-4o', name: 'GPT-4o', provider: 'OpenAI' },
  { id: 'gpt-4.1', name: 'GPT-4.1', provider: 'OpenAI' },
  { id: 'deepseek-reasoner', name: 'DeepSeek Reasoner', provider: 'DeepSeek' },
  { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', provider: 'Google' },
  { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', provider: 'Google' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'Google' },
  { id: 'fireworks/deepseek-r1-0528', name: 'DeepSeek R1 (Fireworks)', provider: 'Fireworks AI' },
  { id: 'llama-v3p3-70b-instruct', name: 'Llama 3.3 70B', provider: 'Meta' },
  { id: 'fireworks/deepseek-v3p1', name: 'DeepSeek V3.1 (Fireworks)', provider: 'Fireworks AI' }
]; 


const COMPONENT_TYPES = [
  { id: 'MARKDOWN', name: 'Rich Text / Markdown' },
  { id: 'TABLE', name: 'Data Table' },
  { id: 'CHART', name: 'Visual Chart' },
  { id: 'JSON', name: 'Raw JSON' }
];


const INITIAL_PROMPTS = [
  {
    id: 'p1',
    title: 'Candidate Fit Score',
    content: 'Assess how well {{candidate_name}} matches the requirements for the job: "{{job_description}}". Provide a compatibility score out of 10.',
    selectedModels: ['gpt-4o', 'gemini-2.5-pro'],
    uiType: 'MARKDOWN',
    winnerModelId: null
  },
  {
    id: 'p2',
    title: 'Interview Question Generator',
    content: 'Based on the JD: "{{job_description}}", generate 3 technical interview questions for {{candidate_name}}.',
    selectedModels: ['gpt-4o'],
    uiType: 'TABLE',
    winnerModelId: null
  },
  {
    id: 'p3',
    title: 'Screening Question Generator',
    content: `You are a recruitment and hiring specialist. Carefully read and analyze the Job Description (JD): "{{job_description}}". Gain a clear understanding of the company’s business, role expectations, key responsibilities, required skills, compliance requirements, and overall hiring intent. Based strictly on this analysis, generate 12 custom screening questions for use in the initial candidate screening stage.


Question Requirements:
- All questions must be answerable strictly as “Yes” or “No.”
- Questions must be job-specific and directly derived from the JD (avoid generic questions).
- Each question should help assess candidate experience, technical capability, compliance readiness, role fit, or ownership level.
- Questions should be clearly judgeable for shortlisting during preliminary screening.
- Avoid open-ended, subjective, or opinion-based questions.


Ranking & Categorization Instructions:
- Rank and sort the 12 questions based on their importance and relevance, using the candidate’s experience level and criticality to the role as key parameters.
- Categorize the questions into two sections: "Strongly Recommended Questions" and "Recommended Questions".
- Exactly 50% of the total questions (6 out of 12) must be categorized as Strongly Recommended.
- The remaining questions must fall under Recommended only.


Justification Requirement:
- Provide a brief justification (1–2 lines each) explaining why the Strongly Recommended questions are critical for evaluating suitability for this role, based on the JD analysis.


Output Format:
Section 1: Strongly Recommended Questions (Ranked 1–6)
Question: [Question text]
Justification: [Reasoning]


Section 2: Recommended Questions (Ranked 7–12)
Question: [Question text]


Ensure the wording is clear, concise, unambiguous, and aligned strictly with the JD.`,
    selectedModels: ['gpt-4o', 'gemini-2.5-pro'],
    uiType: 'MARKDOWN',
    winnerModelId: null
  },
  {
    id: 'p4',
    title: 'Work Style & Personality Analysis',
    content: `ANALYSIS FRAMEWORK:
1. Examine career trajectory: promotions, role changes, tenure patterns
2. Analyze language: action verbs, adjectives, tone, self-description
3. Review achievements: scope, quantifiable results, problem-solving approach
4. Assess skills: breadth vs. depth, soft skills, continuous learning
5. Consider extracurriculars: passions, values, outside interests


OUTPUT REQUIREMENTS:
- Generate EXACTLY 5 bullet points
- Each point must describe a personality trait or work style
- Include brief rationale from resume evidence
- Address: working capacity, adaptability, task willingness, collaboration style
- Format: "• [Trait] - [Brief evidence/explanation]"
- Focus on HOW they work, not WHAT they've done
- Professional, insightful tone


Context: Analyze {{candidate_name}} based on their resume/profile data.`,
    selectedModels: ['gpt-4o', 'claude-3-5-sonnet'],
    uiType: 'MARKDOWN',
    winnerModelId: null
  }
];


/**
 * REUSABLE AUTOCOMPLETE SELECT COMPONENT
 */
const AutocompleteSelect = ({ label, options, value, onChange, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const wrapperRef = useRef(null);


  // Sync internal input with external value when not editing
  useEffect(() => {
    const selectedOption = options.find(o => o.value === value);
    if (selectedOption) {
      setInputValue(selectedOption.label);
    } else if (value === '') {
      setInputValue('');
    }
  }, [value, options]);


  // Handle outside click to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        // Revert to selected value label if text doesn't match a new selection
        const selectedOption = options.find(o => o.value === value);
        if (selectedOption) {
           setInputValue(selectedOption.label);
        } else if (value === '') {
           setInputValue('');
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef, value, options]);


  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(inputValue.toLowerCase())
  );


  const handleSelect = (option) => {
    onChange(option.value);
    setInputValue(option.label);
    setIsOpen(false);
  };


  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setInputValue(newVal);
    setIsOpen(true);
    if (newVal === '') {
      onChange('');
    }
  };


  return (
    <div className="mb-0 position-relative" ref={wrapperRef}>
      <label className="form-label text-xs fw-bold">{label}</label>
      <div className="input-group input-group-sm">
        <input
            type="text"
            className="form-control text-base"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
            autoComplete="off"
            style={{fontSize:'11px'}}
        />
        <span className="input-group-text bg-white border-start-0 text-muted">
           ▼
        </span>
      </div>
     
      {isOpen && !disabled && (
        <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 overflow-auto" style={{ zIndex: 1050, maxHeight: '200px' }}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map(opt => (
              <div
                key={opt.value}
                className={`text-base px-3 py-2 border-bottom border-light ${opt.value === value ? 'bg-primary text-white' : 'text-dark hover-bg-light'}`}
                style={{ cursor: 'pointer', fontSize:'11px' }}
                onClick={() => handleSelect(opt)}
                onMouseEnter={(e) => {
                    if (opt.value !== value) e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                    if (opt.value !== value) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {opt.label}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-muted fst-italic">No matches found</div>
          )}
        </div>
      )}
    </div>
  );
};


/**
 * COMPONENT REGISTRY
 */
const RenderResult = ({ type, data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="text-base visually-hidden" style={{fontSize:"11px"}}>Loading...</span>
        </div>
      </div>
    );
  }


  if (!data) return <div className="text-base text-muted p-4 italic" style={{fontSize:'11px'}}>Execute to see results...</div>;


  switch (type) {
    case 'TABLE':
      const tableData = Array.isArray(data) ? data : [];
      return (
        <div className="table-responsive">
          <table className="table table-sm border-0 m-0">
            <thead className="table-light">
              <tr>
                {Object.keys(tableData[0] || {}).map(k => (
                  <th key={k} className="text-xs fw-bold">{k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, i) => (
                <tr key={i}>
                  {Object.values(row).map((v, j) => (
                    <td key={j} className="text-sm">
                      {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'CHART':
      const chartData = Array.isArray(data) ? data : [];
      return (
        <div className="p-4 text-center bg-white">
          <div className="mb-3 fw-bold text-muted text-xs text-uppercase tracking-wider">Analysis Visualization</div>
          <div className="d-flex align-items-end justify-content-center gap-3" style={{ height: '160px' }}>
            {chartData.map((item, i) => (
              <div key={i} className="text-center w-100" style={{ maxWidth: '32px' }}>
                <div
                  className="bg-primary rounded-top mx-auto"
                  style={{ height: `${item.value || 0}%`, transition: 'height 0.6s ease-out' }}
                  title={`${item.label}: ${item.value}`}
                ></div>
                <div className="mt-2 text-xs text-muted font-monospace">{String(item.label || '')}</div>
              </div>
            ))}
          </div>
        </div>
      );
    case 'JSON':
      return (
        <div className="p-3 bg-dark rounded-bottom">
          <pre className="text-xs text-info m-0" style={{ whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      );
    default:
      let content = typeof data === 'string' ? data : (data?.text || JSON.stringify(data));
      // Basic markdown-like rendering for this demo
      return (
        <div className="p-4 border-start border-4 border-primary bg-white">
          <div className="m-0 text-sm text-dark" style={{ lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{content}</div>
        </div>
      );
  }
};


/**
 * MAIN APP COMPONENT
 */
export default function App() {
  const [prompts, setPrompts] = useState(INITIAL_PROMPTS);
  const [activePrompt, setActivePrompt] = useState(INITIAL_PROMPTS[0]);
  const [results, setResults] = useState({});
  const [executing, setExecuting] = useState(false);
  const [showToast, setShowToast] = useState(null);
 
  // Track which model output the user has selected as the "Winner"
  const [selectedWinnerId, setSelectedWinnerId] = useState(null);
  const [showModelSelector, setShowModelSelector] = useState(false);


  // Tab State for Results
  const [activeResultTab, setActiveResultTab] = useState(null);


  // Dynamic Context State
  const [selectedJobId, setSelectedJobId] = useState(MOCK_JOBS[0].id);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');


  // Derived Context Data
  const filteredCandidates = useMemo(() =>
    MOCK_CANDIDATES.filter(c => c.jobId === selectedJobId),
    [selectedJobId]
  );


  const activeJob = MOCK_JOBS.find(j => j.id === selectedJobId);
  const activeCandidate = MOCK_CANDIDATES.find(c => c.id === selectedCandidateId);


  // Effect to ensure active tab is valid when selected models change
  useEffect(() => {
     if (activePrompt.selectedModels.length > 0) {
        // If current active tab is not in the new selection, reset to the first one
        if (!activeResultTab || !activePrompt.selectedModels.includes(activeResultTab)) {
           setActiveResultTab(activePrompt.selectedModels[0]);
        }
     } else {
        setActiveResultTab(null);
     }
  }, [activePrompt.selectedModels, activeResultTab]);


  const updateActivePrompt = (fields) => {
    setActivePrompt(prev => ({ ...prev, ...fields }));
  };


const handleExecute = async () => {
  setExecuting(true);
  setSelectedWinnerId(null); // Reset selection on new run

  // Reset tab to first model if needed
  if (activePrompt.selectedModels.length > 0) {
    setActiveResultTab(activePrompt.selectedModels[0]);
  }

  const newResults = {};
  for (const modelId of activePrompt.selectedModels) {
    newResults[modelId] = { loading: true };
  }
  setResults(newResults);

  for (const modelId of activePrompt.selectedModels) {
    await new Promise(r => setTimeout(r, 700 + Math.random() * 800));

    let mockData;

    if (activePrompt.uiType === 'TABLE') {
      mockData = [
        { Topic: 'Technical', Question: 'How do you handle React performance?' },
        { Topic: 'Culture', Question: 'Describe a difficult team dynamic.' },
        { Topic: 'Growth', Question: 'Where do you see design systems in 2 years?' }
      ];
    } 
    else if (activePrompt.uiType === 'CHART') {
      mockData = [
        { label: 'Fit', value: 85 },
        { label: 'Skills', value: 70 },
        { label: 'Exp', value: 95 }
      ];
    } 
    else if (activePrompt.title === 'Screening Question Generator') {
      mockData = {
        text: `### Section 1: Strongly Recommended Questions (Ranked 1–6)

**1. Do you have at least 5 years of commercial experience specifically with React.js?**
*Justification: The role explicitly requires 5+ years of React experience as a core competency for a Senior level position.*

**2. Have you implemented strict WCAG 2.1 compliance in a previous production application?**
*Justification: Compliance with WCAG 2.1 is listed as a "strict compliance" requirement in the JD.*

**3. Do you have experience optimizing the performance of large-scale React applications using hooks and memoization?**
*Justification: Performance optimization and deep knowledge of hooks are highlighted as key technical responsibilities.*

**4. Have you used Redux for state management in a professional project?**
*Justification: Redux is listed as a mandatory skill.*

**5. Do you have professional experience integrating React frontends with Node.js backends?**
*Justification: Node.js experience is a specific requirement mentioned in the description.*

**6. Are you available to work onsite/hybrid as per company policy?**
*Justification: Evaluating role fit and logistics is critical for initial screening.*

### Section 2: Recommended Questions (Ranked 7–12)

7. Have you ever migrated a legacy codebase to modern React hooks?
8. Do you have experience writing unit tests using Jest or React Testing Library?
9. Have you worked in an Agile/Scrum environment previously?
10. Are you comfortable conducting code reviews for junior developers?
11. Do you have a notice period of less than 30 days?
12. Have you worked with TypeScript in a React environment?`
      };
    } 
    else if (activePrompt.title === 'Work Style & Personality Analysis') {
      mockData = {
        text: `• **Adaptability** - Frequently shifted between frontend frameworks and backend integration, indicating strong adaptability.

• **Collaboration Style** - Mentioned pairing with designers and mentoring juniors, suggesting a collaborative team-first approach.

• **Task Ownership** - Used action verbs like spearheaded and owned, demonstrating end-to-end responsibility.

• **Continuous Learning** - Completed certifications alongside full-time work, showing proactive growth.

• **Problem-Solving Approach** - Reduced load times by 40%, highlighting measurable optimization focus.`
      };
    } 
    else {
      // ✅ FIXED HERE — removed undefined "model"
      mockData = {
        text: `Final analysis for ${activeCandidate?.name || 'the candidate'} regarding the ${activeJob?.title} role completed by ${modelId}.`
      };
    }

    setResults(prev => ({
      ...prev,
      [modelId]: { loading: false, data: mockData }
    }));
  }

  setExecuting(false);
};



  const handlePublish = () => {
    // If results exist but no winner is selected, prompt user to select one
    if (Object.keys(results).length > 0 && !selectedWinnerId) {
      if (activePrompt.selectedModels.length > 0) {
        setShowModelSelector(true);
        return;
      }
    }


    // Direct publish if already selected or no results (draft mode)
    finalizePublish(selectedWinnerId);
  };


  const finalizePublish = (winnerId) => {
    const winningModel = AVAILABLE_MODELS.find(m => m.id === winnerId);
   
    // Update local library with new config
    setPrompts(prev => prev.map(p =>
      p.id === activePrompt.id
        ? { ...activePrompt, winnerModelId: winnerId }
        : p
    ));


    triggerToast(
      winningModel
        ? `Published! Prompt configured with default model: ${winningModel.name}.`
        : 'Prompt configuration saved (Draft).'
      , 'success'
    );
  };


  const triggerToast = (msg, type = 'success') => {
    setShowToast({ msg, type });
    setTimeout(() => setShowToast(null), 3000);
  };


  return (
    <div className="main-wrapper vh-100 overflow-hidden d-flex flex-column position-relative">
      {/* Shell: Header */}
      <header className="ds-header shadow-sm">
        <div className="ds-brand-row">
          <div className="d-flex align-items-center gap-2">
            {/* <div className="bg-primary rounded p-1 d-inline-flex align-items-center justify-content-center" style={{width: '24px', height: '24px'}}>
              <span className="text-white fw-bold" style={{fontSize: '10px'}}>AI</span>
            </div> */}
            <h3 className="fw-bold m-0 text-base" style={{fontSize:'19px'}}>Prompt Workbench</h3>
          </div>
          <div className="d-flex align-items-center gap-3">
             <span className="text-base information-card">
               Recruitment_Staging_Env
             </span>
          </div>
        </div>
        <div className="ds-nav-row">
          <ul className="nav-tabs-horizontal">
            <li className="text-lg nav-item-h active">Editor & Execution</li>
          </ul>
        </div>
      </header>


      {/* Shell: Content */}
      <div className="d-flex flex-row flex-grow-1 overflow-hidden">
        {/* Left Sidebar: Prompt Library */}
        <aside className="bg-white border-end d-flex flex-column" style={{ width: '280px' }}>
          <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-light">
            <span className="text-base fw-bold text-muted" style={{fontSize:'11px'}}>Prompts</span>
            <button className="btn btn-icon btn-sm" title="New Prompt">+</button>
          </div>
          <div className="overflow-auto flex-grow-1 p-2">
            {prompts.map(p => (
              <div
                key={p.id}
                onClick={() => setActivePrompt(p)}
                className={`card-interactive card-std p-3 mb-2 border ${activePrompt.id === p.id ? 'selected shadow-sm' : ''}`}
              >
                <div className={`fw-bold text-sm mb-1 ${activePrompt.id === p.id ? 'text-primary' : 'text-dark'}`}>{p.title}</div>
                <div className="text-base text-muted text-truncate" style={{fontSize:'11px'}}>{p.content}</div>
                {p.winnerModelId && (
                  <div className="mt-2 badge bg-success bg-opacity-10 text-success border border-success px-2 py-1 rounded-pill" style={{fontSize: '10px'}}>
                    Active: {AVAILABLE_MODELS.find(m => m.id === p.winnerModelId)?.name}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>


        {/* Main Work Area */}
        <main className="ds-content overflow-auto p-4 bg-light">
          <div className="row g-4 justify-content-center h-100">
            {/* Input Configuration Column */}
            <div className="col-xl-6 d-flex flex-column">
              {/* Dynamic Context Selector */}
              <div className="card-std border-0 shadow-sm p-3 bg-white mb-3">
                <h4 className="fw-bold text-muted mb-3" style={{fontSize:'14px'}}>Dynamic Input Context</h4>
              <div className="row g-2">
  <div className="col-md-6">
    <AutocompleteSelect
      label={<span style={{ fontSize: "11px" }}>Active Job Code</span>}
      options={MOCK_JOBS.map(j => ({ value: j.id, label: `${j.id} - ${j.title}` }))}
      value={selectedJobId}
      onChange={(val) => {
        setSelectedJobId(val);
        setSelectedCandidateId(''); // Reset candidate on job change
      }}
      placeholder="Type to search..."
      className="text-base" // input retains text-base class
    />
  </div>
  <div className="col-md-6">
    <AutocompleteSelect
      label={<span style={{ fontSize: "11px" }}>Select Candidate</span>}
      options={filteredCandidates.map(c => ({ value: c.id, label: c.name }))}
      value={selectedCandidateId}
      onChange={setSelectedCandidateId}
      placeholder="Type to search..."
      disabled={!selectedJobId}
      className="text-base" // input retains text-base class
    />
  </div>
</div>

                {activeJob && (
                  <div className="mt-2 p-2 bg-light border rounded">
                    <div className="text-base text-muted mb-1 fw-bold" style={{fontSize:'11px'}}>Job Description (Dynamic):</div>
                    <div className="text-base text-dark opacity-75 text-truncate" style={{maxWidth: '100%', fontSize:'11px'}}>
                      {activeJob.description}
                    </div>
                  </div>
                )}
              </div>


              {/* Prompt Editor */}
              <div className="card-std border-0 shadow-sm p-4 bg-white flex-grow-1 overflow-auto">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h3 className="fw-bold m-0" style={{fontSize:'19px'}}>Logic Configuration</h3>
                  <span className="text-base badge bg-light text-muted border" style={{fontSize:'11px'}}>ID: {activePrompt.id}</span>
                </div>


                <div className="mb-3">
                  <label className="text-base form-label fw-bold text-muted" style={{fontSize:'11px'}}>System Instructions</label>
                  <div className="text-base text-info mb-2" style={{fontSize:'11px'}}>Use tags like <code>{`{{candidate_name}}`}</code> and <code>{`{{job_description}}`}</code></div>
                  <textarea
                    className="text-base form-control font-monospace"
                    rows="8"
                    style={{ resize: 'none', backgroundColor: '#fafafa', fontSize:'11px' }}
                    value={activePrompt.content}
                    onChange={(e) => updateActivePrompt({ content: e.target.value })}
                  ></textarea>
                </div>


                <div className="row g-3 p-3 bg-light rounded border mb-3">
                  <div className="col-md-6">
                    <label className="text-base form-label text-muted" style={{fontSize:'11px', fontWeight:'bold'}}>UI Rendering Type</label>
                    <select style={{fontSize:'11px'}}
                      className="text-base form-select"
                      value={activePrompt.uiType}
                      onChange={(e) => updateActivePrompt({ uiType: e.target.value }) }
                     
                    >
                      {COMPONENT_TYPES.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="text-base form-label text-muted" style={{fontSize:'11px', fontWeight:'bold'}}>Model Test Suite</label>
                    <div className="d-flex flex-wrap gap-2 pt-1">
                      {AVAILABLE_MODELS.map(m => (
                        <div key={m.id} className="form-check m-0">
                          <input
                            className="text-base form-check-input"
                            type="checkbox"
                            id={`chk-${m.id}`}
                            checked={activePrompt.selectedModels.includes(m.id)}
                            onChange={(e) => {
                              const newModels = e.target.checked
                                ? [...activePrompt.selectedModels, m.id]
                                : activePrompt.selectedModels.filter(id => id !== m.id);
                              updateActivePrompt({ selectedModels: newModels });
                            }}
                          />
                          <label className="text-base form-check-label" htmlFor={`chk-${m.id}`} style={{fontSize:'11px'}}>{m.name}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>


                <div className="d-flex gap-2">
                  <button
                    className="btn btn-primary flex-grow-1"
                    onClick={handleExecute}
                    disabled={executing || activePrompt.selectedModels.length === 0 || !selectedCandidateId}
                  >
                    {!selectedCandidateId ? 'Select Candidate to Run' : executing ? 'Executing...' : 'Run Analysis'}
                  </button>
                  <button
                    className={`btn ${selectedWinnerId ? 'btn-success' : 'btn-outline-primary'}`}
                    onClick={handlePublish}
                    title={!selectedWinnerId ? "Publish logic for production use" : "Save this configuration"}
                  >
                    {selectedWinnerId ? `Publish ${AVAILABLE_MODELS.find(m => m.id === selectedWinnerId)?.name} Version` : 'Publish Prompt'}
                  </button>
                </div>
              </div>
            </div>


            {/* Results Column - NOW WITH TABS */}
            <div className="col-xl-6">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="text-base  text-muted m-0" style={{fontSize:'11px', fontWeight:'bold'}}>Model Test Suite</h6>
                <div className="text-base text-muted" style={{fontSize:'11px'}}>Target: {activeCandidate?.name || 'N/A'}</div>
              </div>


              {activePrompt.selectedModels.length === 0 ? (
                <div className="information-card w-100 p-4">
                  Select at least one model.
                </div>
              ) : (
                <>
                  {/* TABS */}
                  <div className="text-base nav-tabs-custom overflow-auto mb-3" style={{fontSize:'11px'}}>
                    {activePrompt.selectedModels.map(modelId => {
                      const model = AVAILABLE_MODELS.find(m => m.id === modelId);
                      return (
                        <button
                          key={modelId}
                          className={`text-base nav-link-custom ${activeResultTab === modelId ? 'active' : ''} text-nowrap`}
                          onClick={() => setActiveResultTab(modelId)} 
                          style={{fontSize:'11px'}}
                        >
                          {model?.name}
                        </button>
                      );
                    })}
                  </div>


                  {/* ACTIVE TAB CONTENT */}
                  {activeResultTab && (
                    <div className="d-flex flex-column gap-3 pb-5">
                      {(() => {
                         const modelId = activeResultTab;
                         const model = AVAILABLE_MODELS.find(m => m.id === modelId);
                         const result = results[modelId];
                         const isWinner = selectedWinnerId === modelId; 
                         
                         return (
                            <div
                              key={modelId}
                              className={`card-std p-0 bg-white overflow-hidden shadow-sm border ${isWinner ? 'border-success border-2' : ''}`}
                              style={isWinner ? { boxShadow: '0 0 0 4px rgba(25, 135, 84, 0.1)' } : {}}
                            >
                              <div className={`card-header p-3 border-bottom d-flex justify-content-between align-items-center ${isWinner ? 'bg-success bg-opacity-10' : 'bg-white'}`}>
                                <div className="d-flex align-items-center gap-2">
                                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: result?.loading ? '#fbbf24' : '#10b981' }}></div>
                                  <span className="text-base fw-bold text-dark" style={{fontSize:'11px'}}>{model?.name}</span>
                                  {isWinner && <span className="badge bg-success text-white" style={{fontSize: '10px'}}>Selected</span>}
                                </div>
                                <span className="text-base text-muted" style={{fontSize:'11px'}}>{model?.provider}</span>
                              </div>
                             
                              <div className="p-0 position-relative">
                                <RenderResult
                                  type={activePrompt.uiType}
                                  data={result?.data}
                                  isLoading={result?.loading}
                                />
                              </div>

 
                              {/* Selection Footer */}
                              {!result?.loading && result?.data && (
                                 <div className="p-2 border-top bg-light d-flex justify-content-end">
                                   <button
                                     className={`btn btn-sm ${isWinner ? 'btn-success' : 'btn-outline-secondary'} text-xs`}
                                     onClick={() => setSelectedWinnerId(modelId)}
                                   >
                                     {isWinner ? '✓ Selected for Production' : 'Select this Output'}
                                   </button>
                                 </div>
                              )}
                            </div>
                         );
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </main>
      </div>


      {/* Model Selection Modal */}
      {showModelSelector && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header border-bottom">
                <h5 className="modal-title text-base fw-bold">Select Default Model</h5>
                <button type="button" className="btn-close" onClick={() => setShowModelSelector(false)}></button>
              </div>
              <div className="modal-body p-4 bg-light">
                <p className="text-sm text-muted mb-3">You have multiple model results. Please select which model's output should be the default configuration for this prompt:</p>
                <div className="list-group shadow-sm">
                  {activePrompt.selectedModels.map(modelId => {
                     const model = AVAILABLE_MODELS.find(m => m.id === modelId);
                     return (
                       <button
                         key={modelId}
                         className="list-group-item list-group-item-action d-flex justify-content-between align-items-center p-3 border-start-0 border-end-0 border-top-0 border-bottom"
                         onClick={() => {
                           setSelectedWinnerId(modelId);
                           setShowModelSelector(false);
                           finalizePublish(modelId);
                         }}
                       >
                         <span className="text-sm fw-bold text-dark">{model?.name}</span>
                         <span className="text-xs text-muted badge bg-light text-dark border">{model?.provider}</span>
                       </button>
                     )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Shell: Feedback Toast */}
      {showToast && (
        <div className="position-fixed bottom-0 end-0 p-4" style={{ zIndex: 1050 }}>
          <div className={`toast show align-items-center text-white ${showToast.type === 'warning' ? 'bg-warning text-dark' : 'bg-dark'} border-0 shadow-lg`}>
            <div className="d-flex p-2">
              <div className="toast-body text-xs fw-bold d-flex align-items-center gap-2">
                <span>{showToast.type === 'success' ? '✔' : '⚠'}</span> {showToast.msg}
              </div>
              <button type="button" className={`btn-close ${showToast.type === 'warning' ? '' : 'btn-close-white'} ms-auto me-2`} onClick={() => setShowToast(null)}></button>
            </div>
          </div>
        </div>
      )}


      {/* EMBEDDED STYLES FROM ATTACHED CSS */}
      <style>{`
        @charset "UTF-8";
        @import url("https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css");
        @import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");


        :root {
          --brand-primary: #0d6efd;
          --brand-bg: #f5f8ff;
          --brand-text: #212529;
          --color-info-bg: #eef2ff;
          --color-success-bg: #d1e7dd; --color-success-text: #198754;
          --color-warning-bg: #fff3cd; --color-warning-text: #856404;
          --color-danger-bg: #f8d7da; --color-danger-text: #dc3545;
          --ui-gray-bg: #F2F4F7;
          --ui-border: #d0d5dd;
          --ui-text-muted: #667085;
          --ui-text-dark: #344054;
          --radius-md: 0.5rem;
          --radius-lg: 0.75rem;
          --font-base: 13.5px;
          --bs-primary: var(--brand-primary);
          --bs-body-font-family: 'Inter', sans-serif;
          --bs-body-bg: var(--ui-gray-bg);
          --bs-border-radius: var(--radius-md);
        }


        body {
          font-family: var(--bs-body-font-family) !important;
          font-size: var(--font-base);
          background-color: var(--bs-body-bg);
        }


        .main-wrapper { background-color: var(--brand-bg); min-height: 100vh; display: flex; flex-direction: column; }
       
        .ds-header {
          background: #fff; border-bottom: 1px solid var(--ui-border);
          position: sticky; top: 0; z-index: 1000;
          display: flex; flex-direction: column;
        }
        .ds-brand-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 0.75rem 1.5rem; border-bottom: 1px solid var(--ui-gray-bg);
        }
        .ds-nav-row {
          padding: 0 1.5rem;
          overflow-x: auto;
          white-space: nowrap;
          background: #fff;
        }
        .nav-tabs-horizontal {
          display: flex; gap: 2rem;
          border-bottom: none;
          margin: 0; padding: 0;
          list-style: none;
        }
        .nav-item-h {
          padding: 1rem 0.25rem;
          cursor: pointer;
          color: var(--ui-text-muted);
          font-weight: 500;
          font-size: 14px;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }
        .nav-item-h.active {
          color: var(--brand-primary);
          border-bottom-color: var(--brand-primary);
        }


        .btn {
          font-weight: 600; padding: 0.5rem 1rem; border-radius: var(--radius-md);
          display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem;
          transition: 0.2s; font-size: 13px; cursor: pointer; border: 1px solid transparent;
        }
        .btn-primary { background-color: var(--brand-primary); border-color: var(--brand-primary); color: #fff; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); }
        .btn-primary:hover { background-color: #0b5ed7; transform: translateY(-1px); }
        .btn-outline-primary { color: var(--brand-primary); border-color: var(--brand-primary); background: #fff; }
        .btn-outline-primary:hover { background-color: var(--color-info-bg); }
        .btn-icon { padding: 8px; color: var(--ui-text-muted); border: 1px solid var(--ui-border); background: #fff; }


        .form-control, .form-select {
          color: #344054; font-size: 14px; font-weight: 500;
          border-radius: 8px; min-height: 34px; border: 1px solid var(--ui-border);
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); background: #fff;
        }


        .card-std {
          background: #fff; border: 1px solid var(--ui-border); border-radius: 12px;
          transition: 0.3s; position: relative;
        }
        .card-interactive {
          cursor: pointer; transition: all 0.2s; border: 1px solid transparent;
        }
        .card-interactive:hover {
          border-color: var(--brand-primary) !important; background-color: #f8faff;
          transform: translateY(-2px);
        }
        .card-interactive.selected {
          border-color: var(--brand-primary) !important; background-color: var(--color-info-bg);
          box-shadow: 0 0 0 1px var(--brand-primary) inset;
        }


        .information-card {
          display: inline-flex; align-items: center; justify-content: center;
          padding: 4px 12px;
          border-radius: 6px; background-color: var(--color-warning-bg);
          color: var(--color-warning-text); border: 1px solid var(--color-warning-text);
          font-size: 11px; font-weight: 700;
        }
       
        .nav-tabs-custom { display: flex; border-bottom: 1px solid var(--ui-border); margin-bottom: 16px; gap: 24px; }
        .nav-link-custom {
          padding: 10px 4px; color: var(--ui-text-muted); font-weight: 500; font-size: 14px;
          background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer; transition: 0.2s;
        }
        .nav-link-custom:hover { color: var(--brand-text); }
        .nav-link-custom.active { color: var(--brand-primary); border-bottom-color: var(--brand-primary); }




        .text-xs { font-size: 0.75rem !important; }
        .text-sm { font-size: 0.875rem !important; }
        .font-monospace { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace !important; }
       
        .border-dashed { border-style: dashed !important; border-width: 2px !important; }
      `}</style>
    </div>
  );
}

